"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HDSSettings = exports.SETTING_TYPES = void 0;
exports.deleteForGood = deleteForGood;
const localizeText_ts_1 = require("../localizeText.js");
/**
 * Known setting event types — each stored as a separate Pryv event.
 */
exports.SETTING_TYPES = {
    preferredLocales: 'settings/preferred-locales',
    theme: 'settings/theme',
    timezone: 'settings/timezone',
    dateFormat: 'settings/date-format',
    unitSystem: 'settings/unit-system',
    displayName: 'contact/display-name',
};
/**
 * Dynamic setting prefixes — one event per key, keyed by content.itemKey.
 * Event type is shared for all settings with the same prefix.
 */
const DYNAMIC_PREFIXES = {
    'preferred-display-': { eventType: 'settings/preferred-display', contentKey: 'itemKey', contentValue: 'value' },
    'preferred-input-': { eventType: 'settings/preferred-input', contentKey: 'itemKey', contentValue: 'value' },
};
const DEFAULTS = {
    preferredLocales: ['en'],
    theme: 'light',
    timezone: 'Europe/Zurich',
    dateFormat: 'DD.MM.YYYY',
    unitSystem: 'metric',
    displayName: null,
};
/**
 * Detect browser defaults for settings that can be inferred.
 */
function browserDefaults() {
    if (typeof navigator === 'undefined')
        return {};
    const result = {};
    try {
        const lang = navigator.language?.split('-')[0];
        if (lang)
            result.preferredLocales = [lang];
    }
    catch { /* ignore */ }
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz)
            result.timezone = tz;
    }
    catch { /* ignore */ }
    return result;
}
function keyForType(type) {
    for (const [key, t] of Object.entries(exports.SETTING_TYPES)) {
        if (t === type)
            return key;
    }
    return null;
}
function applySideEffects(values, key) {
    if (key === 'preferredLocales') {
        try {
            (0, localizeText_ts_1.setPreferredLocales)(values.preferredLocales);
        }
        catch { /* locale may not be supported — ignore */ }
    }
}
/** Find the dynamic prefix config for a key, or null */
function findDynamicPrefix(key) {
    for (const [prefix, config] of Object.entries(DYNAMIC_PREFIXES)) {
        if (key.startsWith(prefix)) {
            return { prefix, ...config, suffix: key.slice(prefix.length) };
        }
    }
    return null;
}
/**
 * Delete an event for good. Pryv's `events.delete` on a live event only trashes it (the result
 * carries `event`); a second call on the trashed event deletes it (`eventDeletion`). An event
 * already trashed elsewhere is deleted by the first call. B-2026-10-26-7: a single call
 * expecting `eventDeletion` threw "eventDeletion missing" and left the setting in place.
 */
async function deleteForGood(connection, id) {
    const first = await connection.apiOne('events.delete', { id });
    if (first?.eventDeletion != null)
        return;
    // pryv's typings know only the trashing answer (`event`) of events.delete, hence the cast
    await connection.apiOne('events.delete', { id }, 'eventDeletion');
}
/** @internal */
let _connection = null;
/** @internal */
let _streamId = null;
/** @internal */
let _cache = {};
/** @internal */
let _values = { ...DEFAULTS };
/** @internal — dynamic settings: key → value */
let _dynamicValues = {};
/** @internal — dynamic settings: key → cached event */
let _dynamicCache = {};
/** @internal */
let _hooked = false;
async function load() {
    if (!_connection || !_streamId)
        return;
    const browser = browserDefaults();
    _values = { ...DEFAULTS, ...browser };
    _cache = {};
    _dynamicValues = {};
    _dynamicCache = {};
    // Collect all event types to fetch (typed + dynamic)
    const typedEventTypes = Object.values(exports.SETTING_TYPES);
    const dynamicEventTypes = Object.values(DYNAMIC_PREFIXES).map(c => c.eventType);
    const allTypes = [...typedEventTypes, ...dynamicEventTypes];
    const settingsEvents = await _connection.apiOne('events.get', { streams: [_streamId], types: allTypes, limit: 200 }, 'events');
    for (const event of settingsEvents) {
        // Try typed settings first
        const key = keyForType(event.type);
        if (key && !_cache[key]) {
            _cache[key] = event;
            _values[key] = event.content;
            continue;
        }
        // Try dynamic settings
        for (const [prefix, config] of Object.entries(DYNAMIC_PREFIXES)) {
            if (event.type === config.eventType && event.content?.[config.contentKey]) {
                const dynKey = prefix + event.content[config.contentKey];
                if (!_dynamicCache[dynKey]) {
                    _dynamicCache[dynKey] = event;
                    _dynamicValues[dynKey] = event.content[config.contentValue];
                }
            }
        }
    }
    applySideEffects(_values, 'preferredLocales');
    _hooked = true;
}
/**
 * HDSSettings — singleton managing user settings as individual Pryv events.
 *
 * Supports two kinds of settings:
 * - **Typed settings**: fixed keys (theme, dateFormat, etc.) with specific event types.
 * - **Dynamic settings**: prefix-based keys (preferred-display-{itemKey}) stored as events
 *   with a shared event type and keyed by content field.
 *
 * Usage:
 *   await HDSSettings.hookToApplication(app);
 *   const locale = HDSSettings.get('preferredLocales');
 *   await HDSSettings.set('theme', 'dark');
 *   await HDSSettings.setDynamic('preferred-display-wellbeing-mood', 'billings');
 */
const HDSSettings = {
    /**
     * Hook to an Application instance — loads settings from the app's baseStream.
     */
    async hookToApplication(app) {
        _connection = app.connection;
        _streamId = app.baseStreamId;
        await load();
    },
    /**
     * Hook to a raw Pryv Connection with a target stream.
     * Used when no Application wrapper is available.
     */
    async hookToConnection(connection, streamId) {
        _connection = connection;
        _streamId = streamId;
        await load();
    },
    /**
     * Get the current value for a typed setting.
     * Also checks dynamic settings for prefix-based keys (e.g. 'preferred-display-wellbeing-mood').
     */
    get(key) {
        if (key in _dynamicValues)
            return _dynamicValues[key];
        return _values[key];
    },
    /**
     * Get all current typed settings values.
     */
    getAll() {
        return { ..._values };
    },
    /**
     * Get all dynamic settings with a given prefix.
     * Returns a map of suffix → value (e.g. { 'wellbeing-mood': 'billings' }).
     */
    getDynamic(prefix) {
        const result = {};
        for (const [key, value] of Object.entries(_dynamicValues)) {
            if (key.startsWith(prefix)) {
                result[key.slice(prefix.length)] = value;
            }
        }
        return result;
    },
    /**
     * Set a typed setting value — persists to HDS server and updates cache.
     * When not hooked (no Pryv connection), accepts the value into the
     * in-memory cache only (memory-only mode — useful for standalone demos
     * where settings don't need to survive the session).
     */
    async set(key, value) {
        if (!_connection || !_streamId) {
            _values[key] = value;
            applySideEffects(_values, key);
            return;
        }
        const eventType = exports.SETTING_TYPES[key];
        const existing = _cache[key];
        if (existing) {
            const updated = await _connection.apiOne('events.update', { id: existing.id, update: { content: value } }, 'event');
            _cache[key] = updated;
        }
        else {
            const created = await _connection.apiOne('events.create', { streamIds: [_streamId], type: eventType, content: value }, 'event');
            _cache[key] = created;
        }
        _values[key] = value;
        applySideEffects(_values, key);
    },
    /**
     * Set a dynamic setting value — persists to HDS server.
     * Key must match a known prefix (e.g. 'preferred-display-wellbeing-mood').
     * Pass null to delete the setting.
     * When not hooked (no Pryv connection), accepts the value into the
     * in-memory map only (memory-only mode — useful for standalone demos).
     */
    async setDynamic(key, value) {
        const dp = findDynamicPrefix(key);
        if (!dp)
            throw new Error(`Unknown dynamic setting prefix for key: "${key}"`);
        if (!_connection || !_streamId) {
            if (value === null || value === undefined) {
                delete _dynamicValues[key];
            }
            else {
                _dynamicValues[key] = value;
            }
            return;
        }
        const existing = _dynamicCache[key];
        if (value === null || value === undefined) {
            if (existing) {
                await deleteForGood(_connection, existing.id);
                delete _dynamicCache[key];
                delete _dynamicValues[key];
            }
            return;
        }
        const content = { [dp.contentKey]: dp.suffix, [dp.contentValue]: value };
        if (existing) {
            const updated = await _connection.apiOne('events.update', { id: existing.id, update: { content } }, 'event');
            _dynamicCache[key] = updated;
        }
        else {
            const created = await _connection.apiOne('events.create', { streamIds: [_streamId], type: dp.eventType, content }, 'event');
            _dynamicCache[key] = created;
        }
        _dynamicValues[key] = value;
    },
    /**
     * Whether settings have been loaded from the server.
     */
    get isHooked() {
        return _hooked;
    },
    /**
     * Reload settings from the server.
     */
    async reload() {
        await load();
    },
    /**
     * Reset to defaults (in-memory only — does not delete server events).
     */
    unhook() {
        _connection = null;
        _streamId = null;
        _cache = {};
        _values = { ...DEFAULTS };
        _dynamicValues = {};
        _dynamicCache = {};
        _hooked = false;
    },
    /**
     * @internal Test-only: inject a setting value and mark as hooked.
     * Works for both typed and dynamic keys.
     */
    _testInject(key, value) {
        if (findDynamicPrefix(key)) {
            _dynamicValues[key] = value;
        }
        else {
            _values[key] = value;
        }
        _hooked = true;
    },
    /**
     * @internal Test-only: remove an injected setting.
     */
    _testClear(key) {
        if (findDynamicPrefix(key)) {
            delete _dynamicValues[key];
        }
        else {
            delete _values[key];
        }
    },
};
exports.HDSSettings = HDSSettings;
exports.default = HDSSettings;
